import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from "@/hooks/use-mobile";
import { useCompanySettings } from '@/context/CompanySettingsContext';
import { CompanySettingsModal } from './CompanySettingsModal';
import VibemailLogo from './VibemailLogo';
import { AnimatedSidebar, AnimatedSidebarBody } from "@/components/ui/animated-sidebar";
import { TooltipProvider } from '@/components/ui/tooltip';
import { openChat } from '@/lib/eventHandlers';
import { createDefaultLinks } from './sidebar/SidebarLinks';
import { NewChatDialog, HelpDialog, DeleteChatDialog } from './sidebar/SidebarDialogs';
import { useChatHistory } from '@/hooks/useChatHistory';
import { useNewChat } from '@/hooks/useNewChat';
import { useSidebarState } from '@/hooks/useSidebarState';
import { SidebarActions } from './sidebar/SidebarActions';
import { SidebarAdmin } from './sidebar/SidebarAdmin';
import { SidebarChatHistory } from './sidebar/SidebarChatHistory';
import { SidebarFooter } from './sidebar/SidebarFooter';

interface AppSidebarProps {
  currentChatId?: string | null;
}

export function AppSidebar({ currentChatId }: AppSidebarProps) {
  const navigate = useNavigate();
  const { user, profile, signOut, isAdmin } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { setIsModalOpen } = useCompanySettings();
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const { 
    chatHistory, 
    isLoadingChats,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteConfirm,
    fetchChatHistory
  } = useChatHistory();

  const { 
    isNewChatDialogOpen, 
    setIsNewChatDialogOpen, 
    handleNewChat 
  } = useNewChat(() => fetchChatHistory(), isMobile, setOpen);

  const handleOpenChat = (chatId: string) => {
    openChat(chatId);
    if (!location.pathname.includes('/builder')) {
      navigate('/builder');
    }
    if (isMobile) {
      setOpen(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: "Abgemeldet",
        description: "Sie wurden erfolgreich abgemeldet."
      });
      navigate('/auth');
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Es gab ein Problem beim Abmelden.",
        variant: "destructive"
      });
    }
  };

  const links = createDefaultLinks(
    () => setIsNewChatDialogOpen(true),
    () => {
      navigate('/account');
      if (isMobile) setOpen(false);
    },
    handleLogout,
    isAdmin
  );

  const actionLinks = [links[0]];
  const adminLinks = links.filter(link => link.label === "Admin Dashboard");
  const otherLinks = links.filter(link => 
    link.label !== "Neue Konversation" && 
    link.label !== "Admin Dashboard"
  );

  if (!user) return null;

  return (
    <TooltipProvider>
      <AnimatedSidebar open={open} setOpen={setOpen}>
        <AnimatedSidebarBody 
          className="fixed left-0 h-full z-50 justify-between gap-10 
          bg-black/95 backdrop-blur-sm border-r border-gray-900 
          shadow-[0_0_15px_rgba(0,171,85,0.15)]"
        >
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            <div className="py-4">
              {open ? (
                <VibemailLogo size="sm" />
              ) : (
                <div className="flex justify-center">
                  <div className="w-8 h-8 rounded-full bg-jade/20 flex items-center justify-center">
                    <span className="font-bold text-jade">V</span>
                  </div>
                </div>
              )}
            </div>
            
            <SidebarActions actionLinks={actionLinks} open={open} />
            <SidebarAdmin adminLinks={adminLinks} open={open} isAdmin={isAdmin} />
            <SidebarChatHistory 
              open={open}
              handleOpenChat={handleOpenChat}
              handleDeleteClick={handleDeleteClick}
            />
            <SidebarFooter
              open={open}
              user={user}
              profile={profile}
              onHelpClick={() => setIsHelpDialogOpen(true)}
              otherLinks={otherLinks}
            />
          </div>
        </AnimatedSidebarBody>
      </AnimatedSidebar>
      
      <NewChatDialog 
        isOpen={isNewChatDialogOpen}
        onOpenChange={setIsNewChatDialogOpen}
        onConfirm={handleNewChat}
      />
      
      <HelpDialog
        isOpen={isHelpDialogOpen}
        onOpenChange={setIsHelpDialogOpen}
      />
      
      <DeleteChatDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
      />
      
      <CompanySettingsModal />
    </TooltipProvider>
  );
}
